import { ActionHandleEventType, type BlockType, FieldInputType } from '@lighthouse/core'

import { controllableBlocks } from '../types'

export const STOP_ACTION_PROPAGATION = 'data-stop-action-propagation'

export const CONTROLLABLE_BLOCK_TYPES = new Set<BlockType>(controllableBlocks)

export const CONTROL_TEXT_FIELD_TYPES = new Set<FieldInputType>([
    'text',
    'number',
    'email',
    'url',
    'phoneNumber',
    'person',
    'date',
    'calendar',
    'relativeSelect',
    'cascade',
    'file',
    'notes',
    'slider'
])

export const ActionIconMap: Record<ActionHandleEventType, string> = {
    [ActionHandleEventType.ACTION]: 'MouseClick',
    [ActionHandleEventType.AUTOMATION]: 'FlowArrow',
    [ActionHandleEventType.CREATE_RECORD]: 'Add',
    [ActionHandleEventType.UPDATE_RECORD]: 'ArrowCounterClockwise',
    [ActionHandleEventType.OPEN_PAGE]: 'BlockLinkToPage',
    [ActionHandleEventType.OPEN_LINK]: 'Link',
    [ActionHandleEventType.SCROLL_TO]: 'LocateTo',
    [ActionHandleEventType.CONTROL]: 'Regulation',
    [ActionHandleEventType.CALL]: 'PhoneIconLibrary',
    [ActionHandleEventType.GO_BACK]: 'Undo',
    [ActionHandleEventType.NOTIFICATION]: 'Bell',
    [ActionHandleEventType.COPY]: 'Duplicate',
    [ActionHandleEventType.DELETE_RECORD]: 'Trash',
    [ActionHandleEventType.CLOSE_FLOAT]: 'CloseOverlay',
    [ActionHandleEventType.DOWNLOAD_FILE]: 'Import',
    [ActionHandleEventType.UPDATE_PAGE_STATE]: 'PageVariable',
    [ActionHandleEventType.REFRESH_PAGE]: 'ArrowCounterClockwise',
    [ActionHandleEventType.SCREEN_SHOT]: 'Image',
    [ActionHandleEventType.WECHAT_PAY]: 'WechatPay',
    [ActionHandleEventType.WECHAT_TEMPLATE_MSG]: 'WechatPay',
    [ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG]: 'MiniProgram',
    [ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION]: 'MiniProgram',
    [ActionHandleEventType.STRIPE]: 'Stripe',
    [ActionHandleEventType.ALIYUN_MESSAGE]: 'aliyun',
    [ActionHandleEventType.OPEN_WECHAT_CUSTOMER_SERVICE]: 'WeChatLogo',
    [ActionHandleEventType.WECHAT_NAVIGATE_TO_MINI_PROGRAM]: 'MiniProgram',
    [ActionHandleEventType.WECHAT_OPEN_CHANNELS_ACTIVITY]: 'MiniProgram',
    [ActionHandleEventType.UPDATE_QUERY_PARAMETER]: 'UpdateURL',
    [ActionHandleEventType.UPDATE_PAGE_SHARE]: 'SquareShareLine',
}

export const ActionNameMap: Record<ActionHandleEventType, string> = {
    [ActionHandleEventType.ACTION]: '调用动作流',
    [ActionHandleEventType.AUTOMATION]: '调用自动化',
    [ActionHandleEventType.CREATE_RECORD]: '创建记录',
    [ActionHandleEventType.UPDATE_RECORD]: '更新记录',
    [ActionHandleEventType.OPEN_PAGE]: '打开页面',
    [ActionHandleEventType.OPEN_LINK]: '打开链接',
    [ActionHandleEventType.SCROLL_TO]: '定位到组件',
    [ActionHandleEventType.CONTROL]: '控制组件',
    [ActionHandleEventType.CALL]: '拨打电话',
    [ActionHandleEventType.GO_BACK]: '返回上一步',
    [ActionHandleEventType.NOTIFICATION]: '显示提示',
    [ActionHandleEventType.COPY]: '复制到剪切板',
    [ActionHandleEventType.DELETE_RECORD]: '删除单条记录',
    [ActionHandleEventType.CLOSE_FLOAT]: '关闭弹窗',
    [ActionHandleEventType.DOWNLOAD_FILE]: '下载',
    [ActionHandleEventType.ALIYUN_MESSAGE]: '发送短信',
    [ActionHandleEventType.WECHAT_PAY]: '微信支付',
    [ActionHandleEventType.WECHAT_TEMPLATE_MSG]: '微信模板消息',
    [ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG]: '小程序授权订阅消息',
    [ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION]: '发送小程序订阅消息',
    [ActionHandleEventType.STRIPE]: 'Stripe',
    [ActionHandleEventType.REFRESH_PAGE]: '刷新页面',
    [ActionHandleEventType.UPDATE_PAGE_STATE]: '更新页面状态',
    [ActionHandleEventType.SCREEN_SHOT]: '生成图片',
    [ActionHandleEventType.OPEN_WECHAT_CUSTOMER_SERVICE]: '打开微信客服',
    [ActionHandleEventType.WECHAT_NAVIGATE_TO_MINI_PROGRAM]: '跳转小程序',
    [ActionHandleEventType.WECHAT_OPEN_CHANNELS_ACTIVITY]: '打开微信视频',
    [ActionHandleEventType.UPDATE_QUERY_PARAMETER]: '更新查询参数',
    [ActionHandleEventType.UPDATE_PAGE_SHARE]: '更新页面分享',
}

export type ActionTypeChildItem = {
    label: string
    value: string
    icon: string
    type?: string
}

export type ActionTypeItem = {
    label: string
    value: string
    icon: string
    children?: ActionTypeChildItem[]
    divider?: boolean
    color?: string
    flowType?: 'action' | 'automation'
}

export type ActionTypeGroup = {
    label: string
    children: ActionTypeItem[]
}

export const actionTypeInfos: ActionTypeGroup[] = [
    {
        label: '执行流程',
        children: [
            {
                label: '调用动作流',
                children: [],
                value: ActionHandleEventType.ACTION,
                flowType: 'action',
                icon: ActionIconMap[ActionHandleEventType.ACTION],
                color: 'var(--color-gray-700)'
            },
            {
                label: '调用自动化',
                children: [],
                value: ActionHandleEventType.AUTOMATION,
                flowType: 'automation',
                icon: ActionIconMap[ActionHandleEventType.AUTOMATION],
                color: 'var(--color-gray-700)'
            }
        ]
    },
    {
        label: '数据处理',
        children: [
            {
                label: '创建记录',
                value: ActionHandleEventType.CREATE_RECORD,
                icon: ActionIconMap[ActionHandleEventType.CREATE_RECORD],
                color: 'var(--color-green-500)'
            },
            {
                label: '更新记录',
                value: ActionHandleEventType.UPDATE_RECORD,
                icon: ActionIconMap[ActionHandleEventType.UPDATE_RECORD],
                color: 'var(--color-green-500)'
            },
            {
                label: '删除单条记录',
                value: ActionHandleEventType.DELETE_RECORD,
                icon: ActionIconMap[ActionHandleEventType.DELETE_RECORD],
                color: 'var(--color-blue-700)'
            },
            {
                label: '更新页面变量',
                value: ActionHandleEventType.UPDATE_PAGE_STATE,
                icon: 'PageVariable',
                color: 'var(--color-blue-700)'
            },
            {
                label: '更新查询参数',
                value: ActionHandleEventType.UPDATE_QUERY_PARAMETER,
                icon: 'UpdateURL',
                color: 'var(--color-blue-700)'
            }
        ]
    },
    {
        label: '操作',
        children: [
            { label: '打开页面', value: ActionHandleEventType.OPEN_PAGE, icon: ActionIconMap[ActionHandleEventType.OPEN_PAGE], color: 'var(--color-blue-700)' },
            { label: '打开链接', value: ActionHandleEventType.OPEN_LINK, icon: ActionIconMap[ActionHandleEventType.OPEN_LINK], color: 'var(--color-blue-700)' },
            { label: '更新页面分享', value: ActionHandleEventType.UPDATE_PAGE_SHARE, icon: ActionIconMap[ActionHandleEventType.UPDATE_PAGE_SHARE], color: 'var(--color-blue-700)' },
            { label: '定位到组件', value: ActionHandleEventType.SCROLL_TO, icon: ActionIconMap[ActionHandleEventType.SCROLL_TO], color: 'var(--color-blue-700)' },
            { label: '控制组件', value: ActionHandleEventType.CONTROL, icon: ActionIconMap[ActionHandleEventType.CONTROL], color: 'var(--color-blue-700)' },
            { label: '拨打电话', value: ActionHandleEventType.CALL, icon: ActionIconMap[ActionHandleEventType.CALL], color: 'var(--color-blue-700)' },
            { label: '返回上一步', value: ActionHandleEventType.GO_BACK, icon: ActionIconMap[ActionHandleEventType.GO_BACK], color: 'var(--color-blue-700)' },
            { label: '显示提示', value: ActionHandleEventType.NOTIFICATION, icon: ActionIconMap[ActionHandleEventType.NOTIFICATION], color: 'var(--color-blue-700)' },
            { label: '复制到剪切板', value: ActionHandleEventType.COPY, icon: ActionIconMap[ActionHandleEventType.COPY], color: 'var(--color-blue-700)' },
            { label: '关闭弹窗', value: ActionHandleEventType.CLOSE_FLOAT, icon: ActionIconMap[ActionHandleEventType.CLOSE_FLOAT], color: 'var(--color-blue-700)' },
            { label: '下载', value: ActionHandleEventType.DOWNLOAD_FILE, icon: ActionIconMap[ActionHandleEventType.DOWNLOAD_FILE], color: 'var(--color-blue-700)' },
            {
                label: '刷新页面数据',
                value: ActionHandleEventType.REFRESH_PAGE,
                icon: 'ArrowCounterClockwise',
                color: 'var(--color-blue-700)'
            },
            {
                label: '生成图片',
                value: ActionHandleEventType.SCREEN_SHOT,
                icon: 'Image',
                color: 'var(--color-blue-700)'
            }
        ]
    }
]

export const thirdPartyActionTypeGroup: ActionTypeGroup = {
    label: '第三方服务/支付',
    children: [
        {
            label: '微信支付',
            value: ActionHandleEventType.WECHAT_PAY,
            icon: 'WechatPay',
            color: 'var(--color-green-500)'
        },
        {
            label: '微信模板消息',
            value: ActionHandleEventType.WECHAT_TEMPLATE_MSG,
            icon: 'WechatPay', // WeChatLogo
            color: 'var(--color-green-500)'
        },
        {
            label: '小程序授权订阅消息',
            value: ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG,
            icon: 'MiniProgram', // WeChatLogo
            color: 'var(--color-green-500)'
        },
        {
            label: '发送小程序订阅消息',
            value: ActionHandleEventType.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION,
            icon: 'MiniProgram', // WeChatLogo
            color: 'var(--color-green-500)'
        },
        {
            label: 'Stripe',
            value: ActionHandleEventType.STRIPE,
            icon: 'Stripe', // WeChatLogo
            color: 'var(--color-black)'
        },
        {
            label: '发送短信',
            value: ActionHandleEventType.ALIYUN_MESSAGE,
            icon: 'aliyun',
            color: 'var(--color-orange-500)'
        },
        {
            label: '打开微信客服',
            value: ActionHandleEventType.OPEN_WECHAT_CUSTOMER_SERVICE,
            icon: ActionIconMap[ActionHandleEventType.OPEN_WECHAT_CUSTOMER_SERVICE],
            color: 'var(--color-blue-700)'
        },
        {
            label: '跳转小程序',
            value: ActionHandleEventType.WECHAT_NAVIGATE_TO_MINI_PROGRAM,
            icon: ActionIconMap[ActionHandleEventType.WECHAT_NAVIGATE_TO_MINI_PROGRAM],
            color: 'var(--color-blue-700)'
        },
        {
            label: '打开微信视频',
            value: ActionHandleEventType.WECHAT_OPEN_CHANNELS_ACTIVITY,
            icon: ActionIconMap[ActionHandleEventType.WECHAT_OPEN_CHANNELS_ACTIVITY],
            color: 'var(--color-blue-700)'
        }
    ]
}

export const flattenActionTypeInfos = (groups: ActionTypeGroup[] = actionTypeInfos) => {
    const hasThirdPartyGroup = groups.some(g => g.label === thirdPartyActionTypeGroup.label)
    const merged = hasThirdPartyGroup ? groups : [...groups, thirdPartyActionTypeGroup]
    return merged.flatMap(g => g.children)
}
